import { KeyboardEvent, forwardRef } from 'react'
import {
    Field,
    Wrapper,
    EventInputWrapper,
    EventTrigger,
} from 'components/shared/Input/styles'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Icon from 'components/shared/Icon'
import { formatDates } from 'helpers/dates'
import { DefaultTheme } from 'styled-components'

interface DatepickerProps {
    id: string
    placeholder?: string
    openCalendar: () => void
    label?: string
    isRequired?: boolean
    isError?: boolean
    errorMessage?: string
    value: string[]
    hasNoMaxWidth?: boolean
    customFormatter?: (date: string) => string
    theme?: DefaultTheme
    testId?: string
    hasDarkMode?: boolean
}

export type Ref = HTMLDivElement

const Datepicker = forwardRef<Ref, DatepickerProps>(
    (
        {
            placeholder,
            id,
            label,
            isRequired = false,
            openCalendar,
            isError,
            errorMessage,
            value,
            hasNoMaxWidth = false,
            customFormatter,
            theme,
            testId,
            hasDarkMode,
        },
        ref
    ) => {
        const iconColor =
            theme?.mode === 'dark' ? 'grayscale_200' : 'secondary_500'

        const valueToDisplay = (() => {
            if (value.length > 0)
                return customFormatter
                    ? customFormatter(value[0])
                    : formatDates(value, true, true)
            return ''
        })()

        const handleKeyDown = (event: KeyboardEvent<HTMLImageElement>) =>
            event.key === 'Enter' && openCalendar()

        return (
            <Box gap={4} isFullWidth>
                {!!label && (
                    <Text
                        as="label"
                        variant="bodySmall"
                        htmlFor={id}
                        color={isError ? 'error' : 'grayscale_900'}
                        hasTransition
                    >
                        {label} {isRequired && '*'}
                    </Text>
                )}

                <EventInputWrapper>
                    <Wrapper
                        hasNoMaxWidth={hasNoMaxWidth}
                        tabIndex={0}
                        onKeyDown={handleKeyDown}
                        ref={ref}
                        hasDarkMode={hasDarkMode}
                    >
                        <Field
                            type="text"
                            id={id}
                            placeholder={placeholder}
                            disabled={true}
                            required={isRequired}
                            isError={isError}
                            value={valueToDisplay}
                            isCustomField
                            theme={theme}
                            hasDarkMode={hasDarkMode}
                        ></Field>
                        <Icon
                            variant="calendar"
                            color={hasDarkMode ? 'primary_500' : iconColor}
                        />
                    </Wrapper>
                    <EventTrigger
                        onClick={() => openCalendar()}
                        data-testid={testId}
                    />
                </EventInputWrapper>

                {isError && !!errorMessage && (
                    <Text variant="bodyExtraSmall" color="error">
                        {errorMessage}
                    </Text>
                )}
            </Box>
        )
    }
)

Datepicker.displayName = 'Datepicker'

export default Datepicker
